<script setup>
import {ref, nextTick} from 'vue';
import { Core as PostcodeCoreApi } from "yubinbango-core2";
import { formatToFullWidth, formatPostalCode, formatToHalfWidth, formatToHalfWidthKana } from './formatters';
import { isValidPostcode, isValidHalfWidthKana, isValidPhoneNumber } from './validators';
import { Error, useError } from "@/components/modules/ErrorMessage";

const { addError, getErrors, hasErrors, clearError, clearErrors } = useError();
const { apiConfig, formConfig } = defineProps(['apiConfig', 'formConfig']);

const input = ref({
	bill_no: '',
	bill_name: '',
	bill_kana: '',
	bill_zip: '',
	bill_adr_1: '',
	bill_adr_2: '',
	bill_adr_3: '',
	bill_adr_4: '',
	bill_phon: '',
	bill_mail: '',
});

const updateInput = (key, value) => {
	input.value[key] = value;
};

const searchAddress = () => {
	clearError('bill_zip');

	if (!isValidPostcode(input.value.bill_zip)) {
		addError('bill_zip', '郵便番号が正しくありません');
		return;
	}

	new PostcodeCoreApi(input.value.bill_zip, (value) => {
		input.value.bill_adr_1 = value.region;
		input.value.bill_adr_2 = value.locality + value.street;
	});
};

const validatePostcode = () => {
	if (!isValidPostcode(input.value.bill_zip)) {
		addError('bill_zip', '郵便番号が正しくありません');
	}
};

const validateKanaName = () => {
	if (!isValidHalfWidthKana(input.value.bill_kana)) {
		addError('bill_kana', 'ﾌﾘｶﾞﾅを半角ｶﾀｶﾅで入力してください');
	}
};

const validatePhoneNumber = () => {
	if (!isValidPhoneNumber(input.value.bill_phon)) {
		addError('bill_phon', '電話番号が正しくありません');
	}
};

const fetchClientId = async () => {
	const data = new URLSearchParams();
	data.append('nonce', apiConfig.nonce);
	data.append('action', 'get_bank_transfer_application_client_id');

	const response = await fetch(apiConfig.endpoint, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body: data.toString(),
	}).then((response) => {
		if (!response.ok)
			throw new Error('Network response was not ok');

		return response.json();
	}).catch((error) => {
		addError('global', 'システムエラーが発生しました。お手数ですが、管理者にお問い合わせください。');
	});

	return response['client_id'] ?? null;
};

const formSubmittedEvent = async (e) => {
	e.preventDefault();
	clearErrors();
	validatePostcode();
	validateKanaName();
	validatePhoneNumber();

	// バリデーションによるエラーチェック
	if (hasErrors()) {
		return false;
	}

	// 送信できる状態になったら、顧客IDを取得する
	const clientId = await fetchClientId();

	// 顧客IDの取得に失敗した場合は続けない
	if (hasErrors()) {
		return false;
	}

	input.value.bill_no = clientId;

	await nextTick(() => {
		e.target.submit();
	});
};
</script>

<template>
	<form :action="formConfig.action" method="post" id="bank-transfer-application-form" @submit="formSubmittedEvent" accept-charset="shift-jis">
		<input type="hidden" name="version" v-model="formConfig.version">
		<input type="hidden" name="bill_method" v-model="formConfig.bill_method">
		<input type="hidden" name="kessai_id" v-model="formConfig.kessai_id">
		<input type="hidden" name="shop_cd" v-model="formConfig.shop_cd">
		<input type="hidden" name="syuno_co_cd" v-model="formConfig.syuno_co_cd">
		<input type="hidden" name="koushin_kbn" v-model="formConfig.koushin_kbn">
		<input type="hidden" name="shop_link" v-model="formConfig.shop_link">
		<input type="hidden" name="shop_phon_hyoji_kbn" v-model="formConfig.shop_phon_hyoji_kbn">
		<input type="hidden" name="shop_mail_hyoji_kbn" v-model="formConfig.shop_mail_hyoji_kbn">
		<input type="hidden" name="shop_pwd" v-model="formConfig.shop_pwd">
		<input type="hidden" name="bill_mail_kbn" v-model="formConfig.bill_mail_kbn">
		<input type="hidden" name="bill_no" v-model="input.bill_no">

		<Error :errors="getErrors('global')" />

		<table>
			<tbody>
			<tr>
				<th><label>氏名（全角）</label></th>
				<td>
					<input type="text" name="bill_name" v-model="input.bill_name" @blur="updateInput('bill_name', formatToFullWidth(input.bill_name))" maxlength="60" required>
					<Error :errors="getErrors('bill_name')" />
				</td>
			</tr>

			<tr>
				<th><label>ﾌﾘｶﾞﾅ（ｶﾀｶﾅ 半角）</label></th>
				<td>
					<input type="text" name="bill_kana" v-model="input.bill_kana" @blur="updateInput('bill_kana', formatToHalfWidthKana(input.bill_kana))" maxlength="30" required>
					<Error :errors="getErrors('bill_kana')" />
				</td>
			</tr>

			<tr>
				<th><label>住所</label></th>
				<td>
					<div class="address">
						<div class="zipcode">
							〒 <input type="text" name="bill_zip" v-model="input.bill_zip" @blur="updateInput('bill_zip', formatPostalCode(input.bill_zip))" maxlength="7" pattern="[0-9]*" placeholder="0000000" required>
							<button @click="searchAddress" type="button">自動入力</button>
						</div>
						<div class="prefecture">
							<div class="col">
								<select name="bill_adr_1" required v-model="input.bill_adr_1">
									<option value="">- 選択してください -</option>
									<option value="北海道">北海道</option>
									<option value="青森県">青森県</option>
									<option value="岩手県">岩手県</option>
									<option value="宮城県">宮城県</option>
									<option value="秋田県">秋田県</option>
									<option value="山形県">山形県</option>
									<option value="福島県">福島県</option>
									<option value="茨城県">茨城県</option>
									<option value="栃木県">栃木県</option>
									<option value="群馬県">群馬県</option>
									<option value="埼玉県">埼玉県</option>
									<option value="千葉県">千葉県</option>
									<option value="東京都">東京都</option>
									<option value="神奈川県">神奈川県</option>
									<option value="新潟県">新潟県</option>
									<option value="富山県">富山県</option>
									<option value="石川県">石川県</option>
									<option value="福井県">福井県</option>
									<option value="山梨県">山梨県</option>
									<option value="長野県">長野県</option>
									<option value="岐阜県">岐阜県</option>
									<option value="静岡県">静岡県</option>
									<option value="愛知県">愛知県</option>
									<option value="三重県">三重県</option>
									<option value="滋賀県">滋賀県</option>
									<option value="京都府">京都府</option>
									<option value="大阪府">大阪府</option>
									<option value="兵庫県">兵庫県</option>
									<option value="奈良県">奈良県</option>
									<option value="和歌山県">和歌山県</option>
									<option value="鳥取県">鳥取県</option>
									<option value="島根県">島根県</option>
									<option value="岡山県">岡山県</option>
									<option value="広島県">広島県</option>
									<option value="山口県">山口県</option>
									<option value="徳島県">徳島県</option>
									<option value="香川県">香川県</option>
									<option value="愛媛県">愛媛県</option>
									<option value="高知県">高知県</option>
									<option value="福岡県">福岡県</option>
									<option value="佐賀県">佐賀県</option>
									<option value="長崎県">長崎県</option>
									<option value="熊本県">熊本県</option>
									<option value="大分県">大分県</option>
									<option value="宮崎県">宮崎県</option>
									<option value="鹿児島県">鹿児島県</option>
									<option value="沖縄県">沖縄県</option>
								</select>
							</div>
							<div class="col">
								<input type="text" name="bill_adr_2" v-model="input.bill_adr_2" maxlength="50" placeholder="市区町村" required>
							</div>
						</div>
						<div class="line"><input type="text" name="bill_adr_3" v-model="input.bill_adr_3" maxlength="50" placeholder="番地" required></div>
						<div class="line"><input type="text" name="bill_adr_4" v-model="input.bill_adr_4" maxlength="50" placeholder="マンション名等"></div>
					</div>
					<div class="notices">
						<p>※ 郵便番号は半角数字のハイフンなしでご入力ください。</p>
					</div>

					<Error :errors="getErrors('bill_zip')" />
					<Error :errors="getErrors('bill_adr_1')" />
					<Error :errors="getErrors('bill_adr_2')" />
					<Error :errors="getErrors('bill_adr_3')" />
					<Error :errors="getErrors('bill_adr_4')" />
				</td>
			</tr>

			<tr>
				<th><label>電話番号</label></th>
				<td>
					<input type="text" name="bill_phon" v-model="input.bill_phon" @blur="updateInput('bill_phon', formatToHalfWidth(input.bill_phon))" maxlength="14" pattern="[0-9\-]*" required>
					<Error :errors="getErrors('bill_phon')" />
				</td>
			</tr>

			<tr>
				<th><label>メールアドレス</label></th>
				<td>
					<input type="email" name="bill_mail" v-model="input.bill_mail" @blur="updateInput('bill_mail', formatToHalfWidth(input.bill_mail))" maxlength="256" required>
					<Error :errors="getErrors('bill_mail')" />
				</td>
			</tr>
			</tbody>
		</table>

		<div class="form-submit">
			<button type="submit">銀行口座設定へ</button>
		</div>
	</form>
</template>
